import { api, busy, status } from "./shared.js";

/**
 * The browser half of setting up an authenticator app.
 *
 * Shared by the sign-in page and `/account` so the ceremony is one ceremony:
 * ask the server for a secret, show the QR and the typed key, take one code
 * back to prove the app has it, then show the backup codes once.
 *
 * The secret is minted server-side (`POST /account/app/setup`) rather than in
 * the browser. It has to be: Better Auth stores it encrypted with the service's
 * own key, and a secret the page invented would be a secret the server could
 * never check. Rendering the QR there too keeps the credential off every chart
 * service on the internet.
 */
export type AppSetupElements = {
  container: HTMLElement | null;
  qr: HTMLElement | null;
  secret: HTMLElement | null;
  form: HTMLFormElement | null;
  input: HTMLInputElement | null;
  submit: HTMLButtonElement | null;
  backup: HTMLElement | null;
  backupList: HTMLElement | null;
  backupDone: HTMLButtonElement | null;
};

export function appSetupElements(): AppSetupElements {
  return {
    container: document.getElementById("app-setup"),
    qr: document.getElementById("app-qr"),
    secret: document.getElementById("app-secret"),
    form: document.getElementById("app-confirm-form") as HTMLFormElement | null,
    input: document.getElementById("app-confirm") as HTMLInputElement | null,
    submit: document.getElementById("app-confirm-submit") as HTMLButtonElement | null,
    backup: document.getElementById("app-backup"),
    backupList: document.getElementById("app-backup-codes"),
    backupDone: document.getElementById("app-backup-done") as HTMLButtonElement | null,
  };
}

/**
 * Wires the block up.
 *
 * `onDone` is called after the person has confirmed a code and dismissed the
 * backup codes, because "the app is set up" is not the end of anything by
 * itself: on the sign-in page it continues a sign-in, and on /account it
 * reloads the list.
 */
export function wireAppSetup(args: {
  el: AppSetupElements;
  copy: Record<string, string>;
  onDone: () => void;
  /** Everything the caller wants hidden while the ceremony runs. */
  hide?: HTMLElement[];
}): { start: () => Promise<void> } {
  const { el, copy } = args;
  let codes: string[] = [];

  async function start(): Promise<void> {
    status("");
    const started = await api("/account/app/setup", { method: "POST", body: JSON.stringify({}) });
    if (!started.ok) {
      status(started.message ?? copy.generic!, "error", started.requestId);
      return;
    }
    for (const node of args.hide ?? []) node.hidden = true;
    if (el.qr) el.qr.innerHTML = typeof started.data?.qr === "string" ? started.data.qr : "";
    if (el.secret) el.secret.textContent = String(started.data?.secret ?? "");
    codes = Array.isArray(started.data?.backup_codes) ? (started.data.backup_codes as string[]) : [];
    if (el.container) el.container.hidden = false;
    el.input?.focus();
  }

  el.form?.addEventListener("submit", async (event) => {
    event.preventDefault();
    busy(el.submit, true, copy.working);
    status("");
    try {
      const result = await api("/api/auth/two-factor/verify-totp", {
        method: "POST",
        body: JSON.stringify({ code: (el.input?.value ?? "").trim() }),
      });
      if (!result.ok) {
        // The server's sentence when it has one. Its own "invalid code" is a
        // protocol string, and the page has a better one for the only two
        // things that are ever wrong: the digits, or the clock.
        status(result.message ?? copy.appCodeWrong!, "error", result.requestId);
        el.input?.select();
        return;
      }
      if (el.container) el.container.hidden = true;
      // The backup codes, once. There is no second chance to read them and the
      // page says so, so this screen waits for an answer rather than passing
      // through.
      if (el.backupList) {
        el.backupList.innerHTML = "";
        for (const code of codes) {
          const li = document.createElement("li");
          li.textContent = code;
          el.backupList.appendChild(li);
        }
      }
      if (codes.length && el.backup) {
        el.backup.hidden = false;
        el.backupDone?.focus();
        return;
      }
      args.onDone();
    } finally {
      busy(el.submit, false);
    }
  });

  el.backupDone?.addEventListener("click", () => args.onDone());

  return { start };
}
